
import { defineComponent, nextTick } from 'vue';
import { QueueManager, PlayQueue } from './play-queue';
import { AudioPlayer } from '../player';

export default defineComponent({
    props: {
        queueManager: {
            type: QueueManager,
            required: true,
        },
        player: {
            type: AudioPlayer,
            required: true,
        },
    },
    created() {
        this.queueManager.onSwitchActive.on((evt) => {
            this.activeQueue = evt.newQueue;
        });
        this.player.onQueueChange.on((evt) => {
            this.playingQueue = evt.newQueue;
        });
    },
    mounted() {
        this.determineOverflow();
        window.addEventListener('resize', this.determineOverflow);
    },
    unmounted() {
        window.removeEventListener('resize', this.determineOverflow);
    },
    data() {
        const playingQueue = this.player.getQueue();
        return {
            playQueues: this.queueManager.getQueues(),
            activeQueue: this.queueManager.getActiveQueue(),
            playingQueue,
            renamingQueue: null as PlayQueue | null,
            actionsShownQueue: null as PlayQueue | null,
            newName: '',
            overflowAmount: 0,
        };
    },
    watch: {
        playQueues() {
            this.determineOverflow();
        },
    },
    methods: {
        determineOverflow() {
            nextTick(() => {
                const list = this.$el.querySelector('ul[role="tablist"]');
                const ownWidth = list.getBoundingClientRect().width;
                const childrenWidth = [...list.querySelectorAll('li')]
                    .map((el) => el.getBoundingClientRect().width)
                    .reduce((x, y) => x + y);
                this.overflowAmount = childrenWidth - ownWidth;
            });
        },
        newQueue() {
            const newQueue = this.queueManager.newQueue();
            this.playQueues = this.queueManager.getQueues();
            this.queueManager.setActiveQueue(newQueue);
            nextTick(() => {
                this.$el
                    .querySelector('.playlist-picker__playlist--active')
                    .focus();
            });
        },
        rename(queue: PlayQueue) {
            this.renamingQueue = queue;
            this.newName = queue.name;
            nextTick(() => {
                const input = document.getElementById(
                    `playQueue-${queue.id}-name`
                ) as HTMLInputElement;
                input.focus();
                input.selectionStart = 0;
                input.selectionEnd = input.value.length;
            });
        },
        applyRename() {
            if (this.renamingQueue) {
                this.renamingQueue.name = this.newName;
            }
            this.renamingQueue = null;
            this.queueManager.saveQueues();
        },
        cancelRename() {
            this.renamingQueue = null;
        },
        isRenaming(queue: PlayQueue) {
            return this.renamingQueue && this.renamingQueue.id === queue.id;
        },
        isActiveQueue(queue: PlayQueue) {
            return queue.id === this.activeQueue.id;
        },
        isPlayingQueue(queue: PlayQueue) {
            return queue.id === this.playingQueue.id;
        },
        showQueue(queue: PlayQueue) {
            this.queueManager.setActiveQueue(queue);
            this.showTabForQueue(queue);
        },
        deleteQueue(queue: PlayQueue) {
            if (
                queue.size() === 0 ||
                window.confirm(
                    `Are you sure you want to delete "${queue.name}"?`
                )
            ) {
                const activeQueueId = this.activeQueue.id;
                this.queueManager.deleteQueue(queue);
                this.playQueues = this.queueManager.getQueues();
                if (this.player.getQueue().id === queue.id) {
                    this.player.stop();
                    this.player.setQueue(this.playQueues[0]);
                }
                if (queue.id === activeQueueId) {
                    this.activateQueue(0);
                }
            }
        },
        activateQueue(index: number) {
            this.showQueue(this.playQueues[index]);
            this.$el
                .querySelector(`[role=tab]:nth-child(${index + 1}`)
                .focus({ preventScroll: true });
        },
        activatePreviousQueue() {
            if (this.playQueues.length <= 1) {
                return;
            }
            const nextQueueIndex =
                this.playQueues.indexOf(this.activeQueue) - 1;
            const focusedIndex =
                nextQueueIndex < 0
                    ? this.playQueues.length - 1
                    : nextQueueIndex;
            this.activateQueue(focusedIndex);
        },
        activateNextQueue() {
            if (this.playQueues.length <= 1) {
                return;
            }
            const nextQueueIndex =
                this.playQueues.indexOf(this.activeQueue) + 1;
            const focusedIndex =
                nextQueueIndex > this.playQueues.length ? 0 : nextQueueIndex;
            this.activateQueue(focusedIndex);
        },
        showTabForQueue(playQueue: PlayQueue) {
            if (this.overflowAmount <= 0) {
                return;
            }
            const tabEl = document.getElementById(`playQueue-${playQueue.id}`);
            if (!tabEl) {
                console.error(
                    "Tried to focus tab that doesn't exist for ",
                    playQueue
                );
                return;
            }
            const tabLeft = tabEl.offsetLeft;
            const tabRight = tabEl.offsetLeft + tabEl?.offsetWidth;

            const list = this.$el.querySelector('ul[role="tablist"]');
            const listWidth = list.offsetWidth;

            if (tabRight - list.scrollLeft > listWidth - 50) {
                list.scrollTo(tabRight - listWidth, 0);
            } else if (tabLeft < list.scrollLeft - 25) {
                list.scrollTo(tabLeft - 25, 0);
            }
        },
        showNextTab() {
            const list = this.$el.querySelector('ul[role="tablist"]');
            const tabs = [...list.querySelectorAll('[role="tab"]')];

            let lastTab = tabs[0];
            for (const tab of tabs) {
                if (
                    tab.offsetLeft + tab.offsetWidth <=
                    list.scrollLeft + list.offsetWidth
                ) {
                    lastTab = tab;
                } else {
                    break;
                }
            }
            const index = Math.min(
                tabs.indexOf(lastTab) + 1,
                this.playQueues.length - 1
            );
            console.log('showing tab', index);
            this.showTabForQueue(this.playQueues[index]);
        },
        showPreviousTab() {
            const list = this.$el.querySelector('ul[role="tablist"]');
            const tabs = [...list.querySelectorAll('[role="tab"]')];

            let lastTab = tabs[0];
            for (const tab of tabs) {
                if (tab.offsetLeft < list.scrollLeft) {
                    lastTab = tab;
                } else {
                    break;
                }
            }
            const index = Math.max(tabs.indexOf(lastTab), 0);
            this.showTabForQueue(this.playQueues[index]);
        },
        handleWheel(evt: WheelEvent) {
            if (this.overflowAmount < 0) {
                return;
            }
            const increase = evt.deltaY * 5;
            const list = this.$el.querySelector('ul[role="tablist"]');
            list.scrollTo(
                Math.max(
                    0,
                    Math.min(
                        this.overflowAmount + 50,
                        list.scrollLeft + increase
                    )
                ),
                0
            );
        },
    },
});
